import { SplitNotification } from '@app/components/checkout/SplitNotification';
import { Card } from '@app/components/various/Card';
import { Status } from '@app/components/various/Status';
import { getOrderPaymentMethods } from '@app/ducks';
import { getAddressLine } from '@app/utils/getAddressLine';
import { isDefined } from '@app/utils/is';
import { isEmpty } from '@app/utils/isEmpty';
import { Order, Payments } from '@typings';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

interface Props {
  order: Order.Closed;
  payment: Payments.Payment;
}

export const CheckoutSuccessPrepaymentCard = ({ order, payment }: Props) => {
  const { createdAt, status, amount, receiptUrl, paymentPluginName } = payment;
  const { billingAddress } = order;
  const { t } = useTranslation(['common', 'payments', 'totals']);
  const paymentMethods = useSelector(getOrderPaymentMethods) ?? [];

  const addressString = React.useMemo(() => {
    return [getAddressLine(billingAddress.address1, billingAddress.address2), billingAddress.city, billingAddress.countryName]
      .filter(part => isDefined(part) && !isEmpty(part.trim()))
      .join(', ');
  }, [billingAddress]);

  const paymentMethodName = paymentMethods.find(method => method.paymentMethod === paymentPluginName)?.name;

  const statusType = {
    failed: 'overdue',
    processing: 'pending',
    success: 'completed',
  } as const;

  const statusLabel = {
    failed: t('payments:failed'),
    processing: t('payments:processing'),
    success: t('payments:paid'),
  } as const;

  return (
    <Card.Wrapper variant="gray">
      <Card.Header>
        <Status type={statusType[status]} /> {statusLabel[status]}
      </Card.Header>
      <Card.Title>{t('payments:prepayment')}</Card.Title>
      <Card.AttributeList>
        <Card.Attribute name={t('common:created')} value={createdAt} />
        <Card.Attribute name={t('common:company')} value={order.billingAddress.company} />
        <Card.Attribute name={t('payments:paid_with')} value={paymentMethodName} />
        <Card.AttributeLink to={receiptUrl} target="_blank" name={t('payments:receipt')} value={t('common:preview')} />
        <Card.Attribute name={t('common:address')} value={addressString} />
        <Card.Attribute name={t('totals:grandtotal_with_tax')} value={order.totals.grandTotalPrice} />
        <Card.Attribute name={t('totals:amount_paid')} value={amount} />
      </Card.AttributeList>
      <SplitNotification messageType="payment" order={order} />
    </Card.Wrapper>
  );
};
